// seo-enhanced.js
// Enhanced SEO system with structured data, rich snippets, and dynamic meta tags
// Replace your existing seo.js with this file

/**
 * Update document title
 */
export const updateTitle = (title) => {
  document.title = title;
};

/**
 * Update or create meta tag
 */
export const updateMetaTag = (name, content, isProperty = false) => {
  const attribute = isProperty ? 'property' : 'name';
  let meta = document.querySelector(`meta[${attribute}="${name}"]`);
  
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, name);
    document.head.appendChild(meta);
  }
  
  meta.setAttribute('content', content);
};

/**
 * Update canonical URL
 */
export const updateCanonical = (url) => {
  let canonical = document.querySelector('link[rel="canonical"]');
  
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  
  canonical.setAttribute('href', url);
};

/**
 * Add or update structured data (JSON-LD)
 */
export const updateStructuredData = (data, id = 'structured-data') => {
  // Remove existing structured data with this ID
  const existing = document.getElementById(id);
  if (existing) {
    existing.remove();
  }

  // Add new structured data
  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

/**
 * HOME PAGE SEO
 */
export const updateHomeSEO = () => {
  const title = 'PhoneScope - Compare Smartphones Globally | Phone Comparison Tool';
  const description = 'Compare smartphones side-by-side with detailed specs, prices in multiple currencies, and expert ratings. Find your perfect phone across 200+ devices.';
  const url = 'https://phonescopeglobal.com';

  updateTitle(title);
  updateMetaTag('description', description);
  updateCanonical(url);

  // Open Graph tags
  updateMetaTag('og:title', title, true);
  updateMetaTag('og:description', description, true);
  updateMetaTag('og:url', url, true);
  updateMetaTag('og:type', 'website', true);
  updateMetaTag('og:image', `${url}/og-image.png`, true);

  // Twitter Card
  updateMetaTag('twitter:card', 'summary_large_image');
  updateMetaTag('twitter:title', title);
  updateMetaTag('twitter:description', description);
  updateMetaTag('twitter:image', `${url}/og-image.png`);

  // Structured Data - Website
  updateStructuredData({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "PhoneScope",
    "url": url,
    "description": description,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${url}/?search={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  }, 'website-schema');

  // Structured Data - Organization
  updateStructuredData({
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "PhoneScope",
    "url": url,
    "logo": `${url}/logo.png`,
    "sameAs": [
      // Add your social media links here when you have them
    ]
  }, 'organization-schema');
};

/**
 * PHONE DETAILS SEO
 */
export const updatePhoneSEO = (phone, currency = 'US', currencySymbol = '$') => {
  const title = `${phone.brand} ${phone.name} - Specs, Price & Review | PhoneScope`;
  const price = phone.price[currency];
  const description = `${phone.brand} ${phone.name}: ${phone.specs.display}, ${phone.specs.processor}, ${phone.specs.camera} camera, ${phone.specs.battery} battery. Price: ${currencySymbol}${price.toLocaleString()}. Compare specs and ratings.`;
  const url = `https://phonescopeglobal.com/phone/${phone._id}`;

  updateTitle(title);
  updateMetaTag('description', description);
  updateCanonical(url);

  // Open Graph
  updateMetaTag('og:title', title, true);
  updateMetaTag('og:description', description, true);
  updateMetaTag('og:url', url, true);
  updateMetaTag('og:type', 'product', true);
  updateMetaTag('og:image', phone.image, true);

  // Twitter Card
  updateMetaTag('twitter:card', 'summary_large_image');
  updateMetaTag('twitter:title', title);
  updateMetaTag('twitter:description', description);
  updateMetaTag('twitter:image', phone.image);

  // Product Structured Data
  const avgScore = Object.values(phone.score).reduce((a, b) => a + b, 0) / Object.values(phone.score).length;
  
  updateStructuredData({
    "@context": "https://schema.org",
    "@type": "Product",
    "name": `${phone.brand} ${phone.name}`,
    "image": phone.image,
    "description": description,
    "brand": {
      "@type": "Brand",
      "name": phone.brand
    },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": currency === 'US' ? 'USD' : currency === 'UK' ? 'GBP' : currency === 'EU' ? 'EUR' : currency === 'India' ? 'INR' : 'ZAR',
      "lowPrice": price,
      "highPrice": price,
      "offerCount": 1
    },
    "aggregateRating": phone.pros && phone.pros.length > 0 ? {
      "@type": "AggregateRating",
      "ratingValue": (avgScore / 100 * 5).toFixed(1),
      "bestRating": "5",
      "worstRating": "1",
      "ratingCount": "1"
    } : undefined,
    "review": phone.pros && phone.pros.length > 0 ? {
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": (avgScore / 100 * 5).toFixed(1),
        "bestRating": "5"
      },
      "author": {
        "@type": "Organization",
        "name": "PhoneScope"
      },
      "reviewBody": phone.pros.join('. ') + '. ' + phone.cons.join('. ')
    } : undefined
  }, 'product-schema');
};

/**
 * COMPARISON PAGE SEO
 */
export const updateComparisonSEO = (phones) => {
  if (!phones || phones.length < 2) {
    updateHomeSEO();
    return;
  }

  const phoneNames = phones.map(p => `${p.brand} ${p.name}`).join(' vs ');
  const title = `Compare ${phoneNames} | PhoneScope`;
  const description = `Side-by-side comparison of ${phoneNames}. Compare specs, prices, cameras, batteries, and performance scores to find the best phone for you.`;
  const url = 'https://phonescopeglobal.com/compare';

  updateTitle(title);
  updateMetaTag('description', description);
  updateCanonical(url);

  // Open Graph
  updateMetaTag('og:title', title, true);
  updateMetaTag('og:description', description, true);
  updateMetaTag('og:url', url, true);
  updateMetaTag('og:type', 'website', true);

  // Twitter Card
  updateMetaTag('twitter:card', 'summary');
  updateMetaTag('twitter:title', title);
  updateMetaTag('twitter:description', description);

  // ItemList Structured Data for comparison
  updateStructuredData({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": phones.map((phone, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": `${phone.brand} ${phone.name}`,
        "image": phone.image,
        "description": `${phone.specs.display}, ${phone.specs.processor}, ${phone.specs.camera} camera`
      }
    }))
  }, 'comparison-schema');
};

/**
 * RANKINGS PAGE SEO
 */
export const updateRankingsSEO = (category = 'all') => {
  const categoryNames = {
    all: 'All Phones',
    flagship: 'Flagship Phones',
    camera: 'Best Camera Phones',
    gaming: 'Best Gaming Phones',
    battery: 'Best Battery Life Phones',
    value: 'Best Value Phones',
    design: 'Best Design Phones'
  };

  const categoryName = categoryNames[category] || categoryNames.all;
  const title = `${categoryName} Rankings 2025 | PhoneScope`;
  const description = `Discover the top-rated ${categoryName.toLowerCase()} based on expert reviews, performance scores, and user ratings. Updated rankings for 2025.`;
  const url = `https://phonescopeglobal.com/rankings/${category}`;

  updateTitle(title);
  updateMetaTag('description', description);
  updateCanonical(url);

  // Open Graph
  updateMetaTag('og:title', title, true);
  updateMetaTag('og:description', description, true);
  updateMetaTag('og:url', url, true);
  updateMetaTag('og:type', 'website', true);

  // Twitter Card
  updateMetaTag('twitter:card', 'summary');
  updateMetaTag('twitter:title', title);
  updateMetaTag('twitter:description', description);
};

/**
 * Generate sitemap data (to be used by backend)
 */
export const generateSitemapData = (phones) => {
  const baseUrl = 'https://phonescopeglobal.com';
  const urls = [];

  // Homepage
  urls.push({
    url: baseUrl,
    priority: 1.0,
    changefreq: 'daily'
  });

  // Compare page
  urls.push({
    url: `${baseUrl}/compare`,
    priority: 0.8,
    changefreq: 'weekly'
  });

  // Rankings pages
  ['all', 'flagship', 'camera', 'gaming', 'battery', 'value', 'design'].forEach(category => {
    urls.push({
      url: `${baseUrl}/rankings/${category}`,
      priority: 0.7,
      changefreq: 'weekly'
    });
  });

  // Individual phone pages
  phones.forEach(phone => {
    urls.push({
      url: `${baseUrl}/phone/${phone._id}`,
      priority: 0.6,
      changefreq: 'monthly'
    });
  });

  return urls;
};